// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as GuildMemberCommands from '@app/features/member/commands/GuildMemberCommands';
import styles from '@app/features/moderation/components/modals/TimeoutMemberModal.module.css';
import {getTimeoutDurationOptions} from '@app/features/moderation/components/modals/TimeoutMemberOptions';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input, Textarea} from '@app/features/ui/components/form/Input';
import {Select as FormSelect} from '@app/features/ui/components/form/Select';
import type {User} from '@app/features/user/models/User';
import {
	DAYS_PER_YEAR,
	SECONDS_PER_DAY,
	SECONDS_PER_HOUR,
	SECONDS_PER_MINUTE,
} from '@app/vendor/date_utils/DateConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';

const logger = new Logger('TimeoutMemberModal');
interface SelectOption<V extends string | number = number> {
	value: V;
	label: string;
}
interface TimeoutMemberModalProps {
	guildId: string;
	targetUser: User;
}

const MAX_TIMEOUT_SECONDS = DAYS_PER_YEAR * SECONDS_PER_DAY;

type CustomDurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';
const CUSTOM_DURATION_MULTIPLIERS: Record<CustomDurationUnit, number> = {
	seconds: 1,
	minutes: SECONDS_PER_MINUTE,
	hours: SECONDS_PER_HOUR,
	days: SECONDS_PER_DAY,
};
export const TimeoutMemberModal: React.FC<TimeoutMemberModalProps> = observer(({guildId, targetUser}) => {
	const {t, i18n} = useLingui();
	const getCustomDurationUnitOptions = useCallback(
		(): ReadonlyArray<SelectOption<CustomDurationUnit>> => [
			{value: 'seconds', label: t`Seconds`},
			{value: 'minutes', label: t`Minutes`},
			{value: 'hours', label: t`Hours`},
			{value: 'days', label: t`Days`},
		],
		[t],
	);
	const timeoutDurationOptions = useMemo(() => getTimeoutDurationOptions(i18n), [i18n]);
	const durationOptions = useMemo<Array<SelectOption<number | 'custom'>>>(() => {
		const baseOptions = timeoutDurationOptions.map((option) => ({
			value: option.value,
			label: option.label,
		}));
		return [...baseOptions, {value: 'custom' as const, label: t`Custom Duration`}];
	}, [timeoutDurationOptions, t]);
	const customDurationUnitOptions = getCustomDurationUnitOptions();
	const [selectedDuration, setSelectedDuration] = useState<number | 'custom'>(timeoutDurationOptions[3].value);
	const [customDurationValue, setCustomDurationValue] = useState('10');
	const [customDurationUnit, setCustomDurationUnit] = useState<CustomDurationUnit>('minutes');
	const [reason, setReason] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const customDurationNumber = Number(customDurationValue);
	const customDurationSeconds =
		Number.isFinite(customDurationNumber) && customDurationNumber > 0
			? customDurationNumber * CUSTOM_DURATION_MULTIPLIERS[customDurationUnit]
			: 0;
	const customDurationError =
		selectedDuration === 'custom'
			? customDurationNumber <= 0
				? t`Duration must be greater than zero.`
				: customDurationSeconds > MAX_TIMEOUT_SECONDS
					? t`Timeout cannot exceed 365 days.`
					: undefined
			: undefined;
	const isCustomDurationValid = selectedDuration !== 'custom' || !customDurationError;
	const effectiveDurationSeconds = selectedDuration === 'custom' ? customDurationSeconds : (selectedDuration as number);
	const handleTimeout = async () => {
		if (selectedDuration === 'custom' && (!isCustomDurationValid || customDurationSeconds <= 0)) {
			return;
		}
		setIsSubmitting(true);
		try {
			const timeoutUntil = new Date(Date.now() + effectiveDurationSeconds * 1000).toISOString();
			const trimmedReason = reason.trim();
			await GuildMemberCommands.timeout(guildId, targetUser.id, timeoutUntil, trimmedReason || null);
			ToastCommands.createToast({
				type: 'success',
				children: <Trans>Successfully timed out {targetUser.tag}</Trans>,
			});
			ModalCommands.pop();
		} catch (error) {
			logger.error('Failed to timeout member:', error);
			ToastCommands.createToast({
				type: 'error',
				children: <Trans>Failed to timeout member. Please try again.</Trans>,
			});
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Timeout ${targetUser.tag}`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<p className={styles.helperText}>
						<Trans>
							Prevent <strong>{targetUser.tag}</strong> from sending messages, reacting, and joining voice channels for
							the specified duration.
						</Trans>
					</p>
					<FormSelect<number | 'custom'>
						label={t`Timeout Duration`}
						description={t`How long this user should be timed out for.`}
						value={selectedDuration}
						onChange={(value) => setSelectedDuration(value)}
						options={durationOptions}
						disabled={isSubmitting}
					/>
					{selectedDuration === 'custom' && (
						<>
							<div className={styles.durationInputs}>
								<Input
									type="number"
									label={t`Custom Duration`}
									min={1}
									step={1}
									value={customDurationValue}
									onChange={(event) => setCustomDurationValue(event.target.value)}
									error={customDurationError}
									disabled={isSubmitting}
								/>
								<FormSelect<CustomDurationUnit>
									label={t`Unit`}
									value={customDurationUnit}
									onChange={(value) => setCustomDurationUnit(value)}
									options={customDurationUnitOptions}
									disabled={isSubmitting}
								/>
							</div>
							<p className={styles.hint}>
								<Trans>Enter a numeric value and choose a unit.</Trans>
							</p>
							<p className={styles.hint}>
								<Trans>
									The timeout cannot exceed {DAYS_PER_YEAR} days ({MAX_TIMEOUT_SECONDS} seconds).
								</Trans>
							</p>
						</>
					)}
					<Textarea
						label={t`Reason (Optional)`}
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						maxLength={512}
						minRows={3}
						disabled={isSubmitting}
					/>
					<p className={styles.hint}>
						<Trans>This reason will be displayed in the Activity Log in Community Settings.</Trans>
					</p>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()} disabled={isSubmitting}>
					<Trans>Cancel</Trans>
				</Button>
				<Button
					variant="danger-primary"
					onClick={handleTimeout}
					disabled={isSubmitting || (selectedDuration === 'custom' && !isCustomDurationValid)}
				>
					<Trans>Timeout</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
